/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BroadcastPeriod, BroadcastPeriodType } from '~core/models/core.models';
import { BroadcastPeriodService } from '~core/services/broadcast-period.service';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import {
  formatDateExpression,
  isIncludedInArray
} from '~backoffice/app/~shared/utils/utils';
import { concat, differenceWith, filter } from 'lodash-es';
import {
  Subject,
  catchError,
  finalize,
  forkJoin,
  of,
  takeUntil,
  tap
} from 'rxjs';
import { Program } from '~core/models/program.models';

@Component({
  standalone: false,
  selector: 'program-schedule',
  templateUrl: './program-schedule.component.html',
  styleUrls: ['./program-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProgramScheduleComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() program: Program | null | undefined;
  cdRef = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef<ProgramScheduleComponent>);
  snackbarService = inject(SnackbarService);
  broadcastPeriodService = inject(BroadcastPeriodService);

  selectedPeriodIndex = 0;

  loadingItemIds: string[] = [];
  includes = isIncludedInArray;
  formatDateExpression = formatDateExpression;

  form:
    | FormGroup<{
        broadcastPeriods: FormArray<
          FormGroup<{
            id: FormControl<string | null>;
            value: FormControl<string | null>;
            duration: FormControl<number | null>;
          }>
        >;
      }>
    | null
    | undefined;

  isLoading = false;
  private _destroy$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('program' in changes) {
      this.initForm();
    }
  }

  ngAfterViewInit(): void {
    this.initForm();
    console.log(this.program?.broadcastPeriods);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.unsubscribe();
  }

  initForm(): void {
    if (this.program) {
      const cronControls = this.program.broadcastPeriods?.length
        ? this.program.broadcastPeriods.map(
            el =>
              new FormGroup({
                id: new FormControl<string | null>(el.id || ''),
                value: new FormControl<string | null>(el.value || '', [
                  Validators.required
                ]),
                duration: new FormControl<number | null>(el.duration || 60, [
                  Validators.min(1)
                ])
              })
          )
        : [];

      this.form = new FormGroup({
        broadcastPeriods: new FormArray(cronControls)
      });
      this.cdRef.detectChanges();
    }
  }

  saveProgram(): void {
    const { broadcastPeriods } = this.form?.value || {};
    if (!this.program || !broadcastPeriods?.length) return;

    const broadcastPeriodsValues = differenceWith(
      broadcastPeriods,
      this.program.broadcastPeriods!,
      (a, b) => {
        if (a.value?.includes(' ')) {
          return a.value === b.value;
        }
        return a.value?.slice(0, 16) === b.value?.slice(0, 16);
      }
    );

    const creates$ = broadcastPeriodsValues
      .map(el =>
        this.broadcastPeriodService
          .create({
            programId: this.program!.id,
            duration: el.duration!,
            value: el.value!,
            type: el.value?.includes(' ')
              ? BroadcastPeriodType.period
              : BroadcastPeriodType.date
          })
          .pipe(
            tap({
              next: period => {
                const periodIndex = broadcastPeriods.findIndex(
                  el => el.value === period.value
                );
                this.periodsControls
                  ?.at(periodIndex)
                  .patchValue({ id: period.id });
              }
            })
          )
      )
      .map(obs$ => obs$.pipe(catchError(() => of(false))));

    // const requests$ = broadcastPeriodsValues
    //   .map(el =>
    //     el.id
    //       ? this.broadcastPeriodService.update(el.id, {
    //           duration: el.duration!,
    //           value: el.value!,
    //           type: el.value?.includes(' ')
    //             ? BroadcastPeriodType.period
    //             : BroadcastPeriodType.date
    //         })
    //       : this.broadcastPeriodService
    //           .create({
    //             programId: this.program!.id,
    //             duration: el.duration!,
    //             value: el.value!,
    //             type: el.value?.includes(' ')
    //               ? BroadcastPeriodType.period
    //               : BroadcastPeriodType.date
    //           })
    //           .pipe(
    //             tap({
    //               next: period => {
    //                 const periodIndex = broadcastPeriods.findIndex(
    //                   el => el.value === period.value
    //                 );
    //                 this.periodsControls
    //                   ?.at(periodIndex)
    //                   .patchValue({ id: period.id });
    //               }
    //             })
    //           )
    //   )
    //   .map(obs$ => obs$.pipe(catchError(() => of(false))));

    this.isLoading = true;

    forkJoin(creates$)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        }),
        tap({
          next: result => {
            const periods = result.filter(
              el => el !== false
            ) as BroadcastPeriod[];

            if (periods.length === 0) {
              return;
            }

            if (periods.length !== result.length) {
              this.snackbarService.open({
                text: `Certains éléments n'ont pas pus être créés`,
                type: 'warn'
              });
              this.dialogRef.close(periods);
              return;
            }
            this.snackbarService.open({
              text: `Le programme a été défini.`,
              type: 'success'
            });
            this.dialogRef.close(periods);
          }
        })
      )
      .subscribe();
  }

  selectForm(index: number): void {
    if (index === this.selectedPeriodIndex) return;
    this.selectedPeriodIndex = index;
  }

  removeForm(index: number): void {
    const periodId = this.periodsControls?.at(index).value.id;
    if (periodId) {
      this.loadingItemIds = concat([...this.loadingItemIds, periodId]);
      this.broadcastPeriodService
        .delete(periodId)
        .pipe(
          takeUntil(this._destroy$),
          finalize(() => {
            this.loadingItemIds = filter(
              this.loadingItemIds,
              el => el !== periodId
            );
            this.cdRef.detectChanges();
          }),
          tap({
            next: () => {
              this.periodsControls?.removeAt(index);
              // this.selectForm(index ? index - 1 : 0);
            }
          })
        )
        .subscribe();
      return;
    }
    this.periodsControls?.removeAt(index);
    this.selectForm(index ? index - 1 : 0);
  }

  addSchedule(): void {
    const precedentDuration =
      this.currentPeriodControl?.get('duration')?.value || 60;

    this.periodsControls?.push(
      new FormGroup({
        id: new FormControl<string | null>(''),
        value: new FormControl<string | null>('', [Validators.required]),
        duration: new FormControl<number | null>(precedentDuration, [
          Validators.min(1)
        ])
      })
    );
    const periodControlsLength = this.periodsControls?.length || 1;
    this.selectedPeriodIndex = periodControlsLength - 1;
  }

  get periodsControls():
    | FormArray<
        FormGroup<{
          id: FormControl<string | null>;
          value: FormControl<string | null>;
          duration: FormControl<number | null>;
        }>
      >
    | undefined {
    return this.form?.get('broadcastPeriods') as FormArray<
      FormGroup<{
        id: FormControl<string | null>;
        value: FormControl<string | null>;
        duration: FormControl<number | null>;
      }>
    >;
  }

  get currentPeriodControl():
    | FormGroup<{
        id: FormControl<string | null>;
        value: FormControl<string | null>;
        duration: FormControl<number | null>;
      }>
    | undefined {
    return this.periodsControls?.at(this.selectedPeriodIndex);
  }
}
